import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { useCompany } from './use-company';
import { toast } from '@/hooks/use-toast';

export type CalendarEvent = Tables<'calendar_events'> & {
  customer?: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    birth_date?: string | null;
    anniversary_date?: string | null;
    last_seen_at?: string | null;
    visit_count_total?: number;
  } | null;
  visit?: {
    id: string;
    started_at: string;
    ended_at?: string | null;
    duration_sec?: number | null;
    customer_id?: string | null;
    notes?: string | null;
    customer?: {
      id: string;
      first_name?: string | null;
      last_name?: string | null;
      phone?: string | null;
      email?: string | null;
    } | null;
  } | null;
  product?: {
    id: string;
    name: string;
    category: string;
    price: number;
  } | null;
  package?: {
    id: string;
    title: string;
    total_net: number;
  } | null;
  sale?: {
    id: string;
    total_net: number;
  } | null;
};

export interface CalendarFilters {
  location?: string;
  startDate?: string;
  endDate?: string;
  eventType?: string;
  status?: string;
}

type UseCalendarOptions = {
  fetchEvents?: boolean;
};

const CALENDAR_EVENT_SELECTS = [
  `
    *,
    customer:customers(id, first_name, last_name, email, phone, birth_date, anniversary_date, last_seen_at, visit_count_total),
    visit:visits(id, started_at:entered_at, customer_id, customer:customers(id, first_name, last_name, phone, email)),
    product:products(id, name, category, price),
    package:packages(id, title, total_net),
    sale:sales(id, total_net)
  `,
  `
    *,
    customer:customers(id, first_name, last_name, email, phone, birth_date, anniversary_date, last_seen_at, visit_count_total),
    product:products(id, name, category, price),
    package:packages(id, title, total_net),
    sale:sales(id, total_net)
  `,
  `
    *,
    customer:customers(id, first_name, last_name, email, phone, birth_date, anniversary_date, last_seen_at, visit_count_total)
  `,
  `*`,
];

const fetchCalendarEventsWithFallback = async (
  companyId: string,
  range?: { startDate: string; endDate: string }
) => {
  let lastError: unknown = null;

  for (const selectClause of CALENDAR_EVENT_SELECTS) {
    let query = supabase
      .from('calendar_events')
      .select(selectClause)
      .eq('company_id', companyId)
      .is('deleted_at', null);

    if (range?.startDate && range?.endDate) {
      query = query.gte('start_date', range.startDate).lte('start_date', range.endDate);
    }

    const { data, error } = await query
      .order('start_date', { ascending: true })
      .order('start_time', { ascending: true });

    if (!error) {
      return (data || []) as CalendarEvent[];
    }

    lastError = error;
  }

  throw lastError;
};

export const useCalendar = (options: UseCalendarOptions = {}) => {
  const { fetchEvents: shouldFetchEvents = true } = options;
  const { currentCompany } = useCompany();
  const queryClient = useQueryClient();

  // Get all calendar events
  const {
    data: events = [],
    isLoading: loading,
    error
  } = useQuery({
    queryKey: ['calendar-events', currentCompany?.id],
    queryFn: async () => {
      if (!shouldFetchEvents) return [];
      if (!currentCompany?.id) return [];

      return fetchCalendarEventsWithFallback(currentCompany.id);
    },
    enabled: !!currentCompany?.id && shouldFetchEvents,
  });

  // Get events by date range
  const getEventsByDateRange = (startDate: string, endDate: string) => {
    return useQuery({
      queryKey: ['calendar-events', currentCompany?.id, 'range', startDate, endDate],
      queryFn: async () => {
        if (!currentCompany?.id) return [];

        return fetchCalendarEventsWithFallback(currentCompany.id, { startDate, endDate });
      },
      enabled: !!currentCompany?.id && !!startDate && !!endDate,
    });
  };

  // Legacy fetchEvents method for backward compatibility
  const fetchEvents = async (filters: CalendarFilters) => {
    // This method is kept for backward compatibility
    // The new approach uses React Query hooks above
    console.warn('fetchEvents is deprecated, use the events from useQuery instead');
  };

  // Create event mutation
  const createEventMutation = useMutation({
    mutationFn: async (eventData: Omit<TablesInsert<'calendar_events'>, 'company_id'>) => {
      if (!currentCompany?.id) throw new Error('No company selected');

      const { data, error } = await supabase
        .from('calendar_events')
        .insert({
          ...eventData,
          company_id: currentCompany.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Etkinlik oluşturuldu",
        description: "Yeni etkinlik başarıyla eklendi.",
      });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Etkinlik oluşturulurken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error creating event:', error);
    },
  });

  // Update event mutation
  const updateEventMutation = useMutation({
    mutationFn: async ({ eventId, updates }: { eventId: string; updates: TablesUpdate<'calendar_events'> }) => {
      const { data, error } = await supabase
        .from('calendar_events')
        .update(updates)
        .eq('id', eventId)
        .eq('company_id', currentCompany?.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Etkinlik güncellendi",
        description: "Etkinlik başarıyla güncellendi.",
      });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Etkinlik güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error updating event:', error);
    },
  });

  // Delete event mutation (soft delete)
  const deleteEventMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const { data, error } = await supabase
        .from('calendar_events')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', eventId)
        .eq('company_id', currentCompany?.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Etkinlik silindi",
        description: "Etkinlik başarıyla silindi.",
      });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Etkinlik silinirken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error deleting event:', error);
    },
  });

  // Legacy addEvent method for backward compatibility
  const addEvent = async (eventData: any) => {
    console.warn('addEvent is deprecated, use createEvent mutation instead');
    return createEventMutation.mutateAsync(eventData);
  };

  // Legacy updateEvent method for backward compatibility
  const updateEvent = async (eventId: string, updates: any) => {
    console.warn('updateEvent is deprecated, use updateEvent mutation instead');
    return updateEventMutation.mutateAsync({ eventId, updates });
  };

  // Legacy deleteEvent method for backward compatibility
  const deleteEvent = async (eventId: string) => {
    console.warn('deleteEvent is deprecated, use deleteEvent mutation instead');
    return deleteEventMutation.mutateAsync(eventId);
  };

  // Auto-generate customer birthday and anniversary events for the current year
  const generateCustomerRecurringEvents = async () => {
    if (!currentCompany?.id) return;

    const { data: customers, error } = await supabase
      .from('customers')
      .select('id, first_name, last_name, birth_date, anniversary_date')
      .eq('company_id', currentCompany.id)
      .or('birth_date.not.is.null,anniversary_date.not.is.null');

    if (error || !customers) return;

    const currentYear = new Date().getFullYear();
    const eventsToCreate: any[] = [];

    // Generate birthday and anniversary events
    customers.forEach(customer => {
      // Birthday events
      if (customer.birth_date) {
        const birthDate = new Date(customer.birth_date);
        const thisYearBirthday = `${currentYear}-${String(birthDate.getMonth() + 1).padStart(2, '0')}-${String(birthDate.getDate()).padStart(2, '0')}`;
        
        eventsToCreate.push({
          company_id: currentCompany.id,
          title: `🎂 ${customer.first_name} ${customer.last_name} - Doğum Günü`,
          description: 'Müşteri doğum günü - özel teklif göndermeyi düşünün',
          start_date: thisYearBirthday,
          event_type: 'birthday' as const,
          category: 'marketing' as const,
          priority: 'medium' as const,
          customer_id: customer.id,
          is_all_day: true,
          is_recurring: true,
          recurrence_pattern: 'yearly',
          recurrence_interval: 1,
          metadata: {
            auto_generated: true,
            original_birth_date: customer.birth_date,
            customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
          },
        });
      }

      // Anniversary events
      if (customer.anniversary_date) {
        const anniversaryDate = new Date(customer.anniversary_date);
        const thisYearAnniversary = `${currentYear}-${String(anniversaryDate.getMonth() + 1).padStart(2, '0')}-${String(anniversaryDate.getDate()).padStart(2, '0')}`;
        
        eventsToCreate.push({
          company_id: currentCompany.id,
          title: `💖 ${customer.first_name} ${customer.last_name} - Yıldönümü`,
          description: 'Müşteri yıldönümü - özel kampanya göndermeyi düşünün',
          start_date: thisYearAnniversary,
          event_type: 'anniversary' as const,
          category: 'marketing' as const,
          priority: 'medium' as const,
          customer_id: customer.id,
          is_all_day: true,
          is_recurring: true,
          recurrence_pattern: 'yearly',
          recurrence_interval: 1,
          metadata: {
            auto_generated: true,
            original_anniversary_date: customer.anniversary_date,
            customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
          },
        });
      }
    });

    if (eventsToCreate.length === 0) return;

    // Check which events already exist to avoid duplicates
    const { data: existingEvents } = await supabase
      .from('calendar_events')
      .select('customer_id, start_date, event_type')
      .eq('company_id', currentCompany.id)
      .in('event_type', ['birthday', 'anniversary'])
      .gte('start_date', `${currentYear}-01-01`)
      .lte('start_date', `${currentYear}-12-31`);

    const existingEventKeys = new Set(
      existingEvents?.map(e => `${e.customer_id}-${e.start_date}-${e.event_type}`) || []
    );

    const newEvents = eventsToCreate.filter(event => 
      !existingEventKeys.has(`${event.customer_id}-${event.start_date}-${event.event_type}`)
    );

    if (newEvents.length > 0) {
      const { error: insertError } = await supabase
        .from('calendar_events')
        .insert(newEvents);

      if (insertError) {
        console.error('Error creating recurring events:', insertError);
      } else {
        console.log(`Created ${newEvents.length} recurring events for ${currentYear}`);
        // Invalidate queries to refresh the calendar
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      }
    }
  };

  // Helper function to get events by date range with recurring event expansion
  const getEventsForDateRange = async (startDate: Date, endDate: Date) => {
    if (!currentCompany?.id) return [];

    const start = startDate.toISOString().split('T')[0];
    const end = endDate.toISOString().split('T')[0];

    return fetchCalendarEventsWithFallback(currentCompany.id, { startDate: start, endDate: end });
  };

  return {
    events,
    loading,
    error,
    fetchEvents, // Legacy method
    getEventsByDateRange,
    getEventsForDateRange,
    addEvent, // Legacy method
    updateEvent, // Legacy method  
    deleteEvent, // Legacy method
    createEvent: createEventMutation.mutate,
    updateEventData: updateEventMutation.mutate,
    deleteEventData: deleteEventMutation.mutate,
    isCreating: createEventMutation.isPending,
    isUpdating: updateEventMutation.isPending,
    isDeleting: deleteEventMutation.isPending,
    generateCustomerRecurringEvents,
    generateCustomerBirthdayEvents: generateCustomerRecurringEvents, // Legacy alias
  };
};

// Legacy function export for backward compatibility
export function useCalendar_Legacy() {
  return useCalendar();
}